import { computed, inject } from 'vue';
import type { ComputedRef } from 'vue';

import { transactionProviderKey as transactionProvider,
  initialValuesTransactionProvider,
} from '@/modules/transaction/constants/transactionProvider.constant';
import { BREB_PAYMENT_TYPE } from '@/shared/breb/constants/brebCommon.constant';

interface UseBrebSendContext {
  /**
   * Indica si el flujo de envío está operando en contexto Bre-B, es decir, con
   * método de pago Bre-B (`quoteData.operationalCost.paymentType === 'BREB'`).
   *
   * Es el discriminador ÚNICO del contexto de envío y es INDEPENDIENTE de la
   * feature flag `feature_breb_b2b`: la flag controla la DISPONIBILIDAD de Bre-B
   * (aguas arriba, en el selector de método de pago del Quoter), mientras que
   * este contexto refleja que el envío ya se realiza con Bre-B. Si el método de
   * pago es Bre-B, la flag ya se aplicó al seleccionarlo; por eso aguas abajo
   * basta el contexto. No mezclar ambas validaciones.
   */
  isBrebSendContext: ComputedRef<boolean>;
}

/**
 * Resuelve el contexto de envío Bre-B del flujo: método de pago Bre-B
 * seleccionado. NO evalúa la feature flag (esa es una validación independiente
 * que vive en el Quoter para gatear la disponibilidad de Bre-B).
 *
 * @returns {UseBrebSendContext} Objeto cuyo `isBrebSendContext` es un
 * `ComputedRef<boolean>` que indica si el envío opera con método de pago Bre-B.
 */
export function useBrebSendContext(): UseBrebSendContext {
  const { quoteData } = inject(
    transactionProvider,
    initialValuesTransactionProvider
  );

  const isBrebSendContext = computed(
    () => quoteData.value.operationalCost?.paymentType === BREB_PAYMENT_TYPE
  );

  return {
    isBrebSendContext,
  };
}
